/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT TO CALL A CONTRACT — ITS IDENTIFIER, ALWAYS (R-57).                    │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **13 Oct 2026, the product owner: the public identifier replaces the human code on every
 * screen and in every address.** `cont_12a3cd5a` is what a contract is called and where it lives;
 * `HSO-C01` stays on the wire (an agent's sentence, an old bookmark) and is still ACCEPTED as an
 * address by `contractMatches` below, but nothing the app draws or links to uses it.
 *
 * What stood here before, for the record — C56:
 * `HSO-C01` is built from a PROJECT's key prefix, so a contract under a client has none. Every
 * screen that printed `contract.code` would print an empty cell — a row that looks like data
 * somebody failed to enter rather than a contract of a different kind.
 *
 * ⚠️ **The identifier, not a dash.** `cont_12a3cd5a` is what `0152` exists for: a name that
 * can be quoted in a URL, a ticket or an integration, and every contract has one.
 */
export function contractLabel(contract: {
  readonly code: string | null;
  readonly identifier: string;
}): string {
  return contract.identifier;
}

/** The same, for a line item that carries its contract's two names. */
export function lineItemContractLabel(item: {
  readonly contractCode: string | null;
  readonly contractIdentifier: string;
}): string {
  return item.contractIdentifier;
}

/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ 3 Nov 2026 · WHAT A CONTRACT FILTER OR PICKER OFFERS — ITS NAME.          │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * The product owner: *"a contract filter shows `cont_12a3cd5a`, nobody knows which one that is."*
 * R-57 keeps the identifier for addresses and for copy; an OPTION in a list is chosen by reading,
 * so it is labelled with the contract's name, the identifier riding beside it as secondary text
 * (`SelectOption.hint`). Only when two contracts in the SAME list share a name does the project's
 * name join the label — `Support · Haystack` — so the common case stays short.
 *
 * ⚠️ **One helper for every contract option**, so a third screen does not invent a third format.
 */
export interface ContractOptionSource {
  readonly id: string;
  readonly name: string;
  readonly identifier: string;
  readonly projectName?: string | null | undefined;
  /** For an adaptive filter that narrows by project; the label never reads it. */
  readonly projectId?: string | null | undefined;
}

export interface ContractOption {
  readonly value: string;
  readonly label: string;
  /** The identifier — the kit `Select`'s muted right-aligned text. */
  readonly hint: string;
}

/** The names that occur more than once in `names` — the ones whose label needs its project. */
export function repeatedNames(names: readonly string[]): ReadonlySet<string> {
  const seen = new Set<string>();
  const repeated = new Set<string>();
  for (const name of names) {
    const key = name.trim().toLocaleLowerCase();
    if (seen.has(key)) repeated.add(key);
    seen.add(key);
  }
  return repeated;
}

/** One contract's option label, given the list's repeated names. */
export function contractNameLabel(
  contract: { readonly name: string; readonly projectName?: string | null | undefined },
  repeated: ReadonlySet<string>,
): string {
  const name = contract.name.trim();
  const project = contract.projectName ?? '';
  return repeated.has(name.toLocaleLowerCase()) && project !== '' ? `${name} · ${project}` : name;
}

/**
 * The options for a contract filter or picker: name as the label, identifier as the hint, the
 * project only where names repeat. Duplicates by id collapse to one; order is by label.
 */
export function contractOptions(contracts: readonly ContractOptionSource[]): ContractOption[] {
  const unique = [...new Map(contracts.map((one) => [one.id, one])).values()];
  const repeated = repeatedNames(unique.map((one) => one.name));
  return unique
    .map((one) => ({
      value: one.id,
      label: contractNameLabel(one, repeated),
      hint: one.identifier,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** The contract options a list of LINE ITEMS implies — one per contract they belong to. */
export function lineItemContractOptions(
  items: readonly {
    readonly contractId: string;
    readonly contractName: string;
    readonly contractIdentifier: string;
    readonly projectName?: string | null | undefined;
  }[],
): ContractOption[] {
  return contractOptions(
    items.map((one) => ({
      id: one.contractId,
      name: one.contractName,
      identifier: one.contractIdentifier,
      projectName: one.projectName,
    })),
  );
}

/**
 * A line item's option label inside a picker: `<contract name> · <line item name>`, with the
 * project after the contract only when that contract name repeats in the list.
 */
export function lineItemOptionLabel(
  item: {
    readonly contractName: string;
    readonly name: string;
    readonly projectName?: string | null | undefined;
  },
  repeated: ReadonlySet<string> = new Set(),
): string {
  return `${contractNameLabel({ name: item.contractName, projectName: item.projectName }, repeated)} · ${item.name}`;
}

/**
 * The repeated CONTRACT names among line items — counted per contract, not per line item, so two
 * line items of one contract do not make its name look ambiguous.
 */
export function repeatedContractNames(
  items: readonly { readonly contractId: string; readonly contractName: string }[],
): ReadonlySet<string> {
  const byContract = new Map(items.map((one) => [one.contractId, one.contractName]));
  return repeatedNames([...byContract.values()]);
}

/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C56 · THE ADDRESS OF A CONTRACT, WHICH IS NOW EITHER OF ITS TWO NAMES.  │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * `/contracts/HSO-C01` is P2-9's shape and it assumes a code. A client-scoped contract has none,
 * so its address is its identifier — `/contracts/cont_12a3cd5a` — which is exactly the job
 * `0152` describes for it: *"what a URL, a ticket and an integration can quote"*.
 *
 * ⚠️ **One route, two accepted names, matched HERE rather than in three places.** The contracts
 * route resolved `candidate.code === params.code` in three spots; each would have had to learn
 * the fallback separately, and the one that was missed would 404 on a real contract.
 */
/* ⚠️ R-57 · links are identifiers now; a CODE is still accepted, so an agent's `HSO-C01`, a mail
   and a bookmark from before the ruling still open the right page. */
export function contractMatches(
  contract: { readonly code: string | null; readonly identifier: string },
  address: string | null | undefined,
): boolean {
  if (address === null || address === undefined) return false;
  return contract.code === address || contract.identifier === address;
}
